import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import Attachments, { TAttachment } from '@/Pages/Run/components/Attachments';
import { Button } from '@/design-system';

import useToast from '../Toast/useToast';

export type TAttachmentWithFile = TAttachment & {
  file?: File;
};

interface AttachmentEditorProps {
  attachments: TAttachmentWithFile[];
  setAttachments: Dispatch<SetStateAction<TAttachmentWithFile[]>>;
  onReset: () => void;
  isCreationMode?: boolean;
  isFromRun?: boolean;
}

export function AttachmentEditor({
  attachments,
  setAttachments,
  onReset,
  isCreationMode,
  isFromRun,
}: AttachmentEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createToast } = useToast();

  const handleDelete = (attachment: TAttachment) => {
    setAttachments((prev) => {
      const attachmentToDelete = prev.find((a) => a.url === attachment.url);
      if (attachmentToDelete && attachmentToDelete.file) {
        URL.revokeObjectURL(attachmentToDelete.url);
      }
      return prev?.filter((a) => a.url !== attachment.url);
    });
  };

  useEffect(() => {
    // Cleanup function to revoke all object URLs when component unmounts
    return () => {
      attachments.forEach((attachment) => {
        if (attachment.file) {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // check that no 2 files have the same name
    if (
      files.some((f, i) => files.findIndex((f2) => f2.name === f.name) !== i)
    ) {
      createToast({
        title: 'Duplicate attachment(s) names',
        description: `You cannot upload files with the same name`,
      });
      // Behavior is undefined if more than one file has the same name, so we stop here
      return;
    }

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

    const tooLargeFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (tooLargeFiles.length > 0) {
      createToast({
        title: 'File(s) too large',
        description: `${tooLargeFiles
          .map((f) => f.name)
          .join(', ')} exceeds the 20MB size limit`,
      });
    }

    const duplicateFiles = files.filter((file) =>
      attachments.some((a) => a.name === file.name)
    );

    if (duplicateFiles.length > 0) {
      createToast({
        title: 'Duplicate attachment(s) names',
        description: `There are already attachments named ${duplicateFiles
          .map((f) => f.name)
          .join(', ')}`,
      });
    }

    const validFiles = files.filter(
      (file) => file.size <= MAX_FILE_SIZE && !duplicateFiles.includes(file)
    );

    const newAttachments: TAttachmentWithFile[] = validFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      file: file,
      contentType: file.type,
      contentLength: file.size.toString(),
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);

    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onRename = (attachment, newName) => {
    if (newName === '') {
      createToast({
        title: 'Invalid attachment name',
        description: `Attachment names cannot be empty`,
      });
      return false;
    }
    if (
      attachments.find((a) => a.name === newName && attachment.url !== a.url)
    ) {
      createToast({
        title: 'Duplicate attachment name',
        description: `There is already an attachment named ${newName}`,
      });
      return false;
    }
    setAttachments((prev) =>
      prev?.map((a) => {
        if (a.url === attachment.url) {
          return { ...a, name: newName };
        }
        return a;
      })
    );
    return true;
  };

  return (
    <>
      <div className="col-span-2">
        <Attachments
          attachments={attachments.map(({ file: _, ...rest }) => rest)}
          onDelete={handleDelete}
          onRename={isFromRun ? undefined : onRename}
          className="p-0"
          gridClassName="max-h-[250px] w-full"
        />
      </div>
      <div className="-ml-.5 mt-2 flex gap-2">
        {(isCreationMode || !isFromRun) && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              multiple
            />
            <Button
              size="md"
              color="primary"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Files
            </Button>
          </>
        )}
        {!isCreationMode && (
          <Button
            size="sm"
            color="primary"
            variant="outlined"
            onClick={onReset}
          >
            Reset
          </Button>
        )}
      </div>
    </>
  );
}
